import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';

const reflectionMaps = new WeakMap();

export function racingCarReflections(renderer) {
  if (!reflectionMaps.has(renderer)) {
    const studio = new RoomEnvironment();
    const generator = new THREE.PMREMGenerator(renderer);
    reflectionMaps.set(renderer, generator.fromScene(studio, 0.04).texture);
    studio.dispose();
    generator.dispose();
  }
  return reflectionMaps.get(renderer);
}

// Recolour only painted red pixels. The original atlas also contains the
// headlights, BMW badges, grille, vents and panel seams: retain all of them.
function racingPaintMaps(original, colour) {
  const image = original.image;
  const canvas = document.createElement('canvas');
  canvas.width = Math.min(2048, image.width);
  canvas.height = Math.round(image.height * canvas.width / image.width);
  const context = canvas.getContext('2d', { willReadFrequently: true });
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
  const roughCanvas = document.createElement('canvas');
  roughCanvas.width = canvas.width;
  roughCanvas.height = canvas.height;
  const roughContext = roughCanvas.getContext('2d');
  const roughness = roughContext.createImageData(canvas.width, canvas.height);
  // Deepen the coloured base coat in linear space; clearcoat reflections stay
  // bright, so highlights read clearly against the richer paint underneath.
  const tint = new THREE.Color(colour).multiplyScalar(.32).convertLinearToSRGB();
  for (let i = 0; i < pixels.data.length; i += 4) {
    const r = pixels.data[i], g = pixels.data[i + 1], b = pixels.data[i + 2];
    const paint = r > 35 && r > g * 1.65 && r > b * 1.65;
    if (paint) {
      const shade = Math.min(1, .72 + r / 900);
      pixels.data[i] = tint.r * 255 * shade;
      pixels.data[i + 1] = tint.g * 255 * shade;
      pixels.data[i + 2] = tint.b * 255 * shade;
    }
    const finish = paint ? 46 : 165;
    roughness.data.set([finish, finish, finish, 255], i);
  }
  context.putImageData(pixels, 0, 0);
  roughContext.putImageData(roughness, 0, 0);
  const texture = (source, colorSpace) => {
    const map = original.clone();
    map.source = new THREE.Source(source);
    map.colorSpace = colorSpace;
    map.needsUpdate = true;
    return map;
  };
  return { map: texture(canvas, THREE.SRGBColorSpace), roughnessMap: texture(roughCanvas, THREE.NoColorSpace) };
}

export function createRacingCarAppearance(scene, colour, reflections) {
  const clone = scene.clone(true);
  const materials = new Map();
  clone.traverse(child => {
    if (!child.isMesh) return;
    child.castShadow = true;
    child.receiveShadow = true;
    const finish = original => {
      if (materials.has(original)) return materials.get(original);
      let material = original.clone();
      if (original.name === 'Body') {
        material = new THREE.MeshPhysicalMaterial({
          name: 'Racing metallic paint',
          ...racingPaintMaps(original.map, colour),
          normalMap: original.normalMap, normalScale: original.normalScale,
          aoMap: original.aoMap, aoMapIntensity: .75,
          metalness: .45, roughness: 1, clearcoat: 1,
          clearcoatRoughness: .045, envMap: reflections, envMapIntensity: 1.35,
          ior: 1.5, specularIntensity: 1,
        });
      } else if (original.name === 'glass') {
        material = new THREE.MeshPhysicalMaterial({
          name: 'Smoked motorsport glass', color: '#192630', metalness: .12,
          roughness: .055, clearcoat: 1, clearcoatRoughness: .035,
          envMap: reflections, envMapIntensity: 1.1,
        });
      } else if (original.name.includes('TireWheel')) {
        material.roughness = .68;
        material.envMap = reflections;
        material.envMapIntensity = .6;
      }
      materials.set(original, material);
      return material;
    };
    child.material = Array.isArray(child.material) ? child.material.map(finish) : finish(child.material);
  });
  // Preserve the original car's exact scale, centre and road contact height.
  const size = new THREE.Box3().setFromObject(clone).getSize(new THREE.Vector3());
  clone.scale.setScalar(5.5 / Math.max(size.x, size.y, size.z));
  const bounds = new THREE.Box3().setFromObject(clone);
  const centre = bounds.getCenter(new THREE.Vector3());
  clone.position.x -= centre.x;
  clone.position.z -= centre.z;
  clone.position.y -= bounds.min.y;
  const car = new THREE.Group();
  car.add(clone);
  const carbon = new THREE.MeshPhysicalMaterial({ color: '#151a20', metalness: .35, roughness: .3, clearcoat: .8, clearcoatRoughness: .16, envMap: reflections });
  const metal = new THREE.MeshStandardMaterial({ color: '#656d78', metalness: .9, roughness: .22, envMap: reflections });
  const part = (dimensions, position, material = carbon) => {
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(...dimensions), material);
    mesh.position.set(...position);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    car.add(mesh);
    return mesh;
  };
  // GT-style rear wing, metal uprights, endplates, low splitter and side skirts.
  part([2.14, .075, .36], [0, 1.52, -2.04]).rotation.x = -.08;
  [-.73, .73].forEach(x => part([.055, .36, .1], [x, 1.31, -2.04], metal));
  [-1.08, 1.08].forEach(x => part([.035, .24, .43], [x, 1.55, -2.04]));
  part([2.06, .055, .32], [0, .15, 2.47]);
  [-1.0, 1.0].forEach(x => part([.09, .11, 3.25], [x, .21, .03]));
  // Wraparound LED tail-light signatures and a finned rear diffuser.
  const tailLight = new THREE.MeshStandardMaterial({ color: '#ee172d', emissive: '#ff142a', emissiveIntensity: 2, roughness: .2, metalness: .1 });
  [-1, 1].forEach(sign => {
    const path = new THREE.CatmullRomCurve3([
      new THREE.Vector3(sign * .48, .86, -2.54),
      new THREE.Vector3(sign * .77, .87, -2.52),
      new THREE.Vector3(sign * .95, .94, -2.4),
    ]);
    car.add(new THREE.Mesh(new THREE.TubeGeometry(path, 10, .026, 6, false), tailLight));
  });
  part([1.42, .13, .22], [0, .19, -2.48]);
  [-.5, 0, .5].forEach(x => part([.035, .19, .36], [x, .2, -2.43]));
  return car;
}
